import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

const PrintOptionsSelector = ({ totalPages, preferences, setPreferences }) => {
  const updatePreference = (key, value) => {
    setPreferences({ ...preferences, [key]: value });
  };

  const incrementCopies = () => {
    updatePreference('copies', Math.min(100, preferences.copies + 1));
  };

  const decrementCopies = () => {
    updatePreference('copies', Math.max(1, preferences.copies - 1));
  };

  return (
    <>
      {/* Pages and Copies */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label>Total Pages</Label>
          <div
            className="flex h-10 w-full items-center rounded-md border border-input bg-secondary px-3 py-2 text-sm font-medium text-foreground"
            data-testid="pages-display"
          >
            {totalPages}
          </div>
          <p className="text-xs text-muted-foreground">Auto-detected</p>
        </div>

        <div className="space-y-2">
          <Label>Number of Copies</Label>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={decrementCopies}
              disabled={preferences.copies <= 1}
              data-testid="copies-decrement-btn"
              className="h-10 w-10 shrink-0"
            >
              <Minus className="w-4 h-4" />
            </Button>
            <div
              className="flex-1 h-10 flex items-center justify-center rounded-md border border-input bg-background text-sm font-medium"
              data-testid="copies-value"
            >
              {preferences.copies}
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={incrementCopies}
              disabled={preferences.copies >= 100}
              data-testid="copies-increment-btn"
              className="h-10 w-10 shrink-0"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Paper Size */}
      <div className="space-y-2">
        <Label>Size of Paper</Label>
        <div className="grid grid-cols-2 gap-2">
          {['A4', 'A3'].map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => updatePreference('paper_size', size)}
              data-testid={`paper-size-${size.toLowerCase()}-btn`}
              className={`h-10 rounded-md border text-sm font-medium transition-colors ${
                preferences.paper_size === size
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'bg-background text-foreground border-input hover:bg-secondary'
              }`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Color Mode */}
      <div className="space-y-2">
        <Label>Color Mode</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { value: 'bw', label: 'Black & White' },
            { value: 'color', label: 'Color' }
          ].map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => updatePreference('color_mode', opt.value)}
              data-testid={`color-mode-${opt.value}-btn`}
              className={`h-10 rounded-md border text-sm font-medium transition-colors ${
                preferences.color_mode === opt.value
                  ? 'bg-accent text-accent-foreground border-accent'
                  : 'bg-background text-foreground border-input hover:bg-secondary'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Duplex */}
      <div className="flex items-center justify-between">
        <Label htmlFor="duplex">Do you want both sides printing?</Label>
        <Switch
          id="duplex"
          checked={preferences.duplex}
          onCheckedChange={(checked) => updatePreference('duplex', checked)}
          data-testid="duplex-switch"
        />
      </div>
    </>
  );
};

export default PrintOptionsSelector;
